import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { getShareableReport } from "../../../../services/dynamicActionPlanApi";
import AppReportToolbar from "../../../report/AppReportToolbar";
import "./SharedMobilityReport.scss";

// Care-mode → printable template. Mirrors REPORT_PRINT_URLS in
// public/mobility-report/app.js. The shared view re-renders the exact same
// static print template used by Preview/Share, feeding it the stored payload
// through the template's own "#data=" decoder (no template changes needed).
const PRINT_TEMPLATES = {
  Safety:
    "/mobility-report-react-html-v87/standalone/print/Mobility_Primary_Care_for_print_v87.html",
  home_care:
    "/mobility-report-react-html-v87/standalone/print/Mobility_Home_Care_for_print_v87.html",
  pharmacy:
    "/mobility-report-react-html-v87/standalone/print/Mobility_Pharmacy_for_print_v87.html",
  senior_living:
    "/mobility-report-react-html-v87/standalone/print/Mobility_Senior_Living_for_print_v87.html",
  rehab:
    "/mobility-report-react-html-v87/standalone/print/Mobility_Rehab_for_print_v87.html",
  Longevity:
    "/mobility-report-react-html-v87/standalone/print/Mobility_Longevity_for_print_v87.html",
  prod_us:
    "/mobility-report-react-html-v87/standalone/print/Mobility_Prod_US_for_print_v87.html",
};

// Prod_US prints on US Letter; every other mode prints on A4.
const LETTER_MODES = new Set(["prod_us"]);
const LETTER_CLASS = "shared-mobility-letter";

// base64url(JSON(payload)) — the plain "data=" encoding the print templates
// already accept. This lives in the iframe's own URL (not the shared link the
// user sees), so length is irrelevant and no compression is needed here.
const encodePayload = (payload) => {
  // base64 of the UTF-8 bytes (matches the templates' atob -> TextDecoder path).
  const b64 = btoa(unescape(encodeURIComponent(JSON.stringify(payload))));
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

const PRINT_STYLE_ATTRIBUTE = "data-shared-mobility-print-style";

const copyCanvasImages = (sourcePage, clonedPage) => {
  const sourceCanvases = sourcePage.querySelectorAll("canvas");
  const clonedCanvases = clonedPage.querySelectorAll("canvas");

  sourceCanvases.forEach((sourceCanvas, index) => {
    const clonedCanvas = clonedCanvases[index];
    if (!clonedCanvas) return;

    try {
      const image = document.createElement("img");
      image.src = sourceCanvas.toDataURL("image/png");
      image.alt = "";
      image.className = sourceCanvas.className;
      image.style.cssText = sourceCanvas.style.cssText;
      image.width = sourceCanvas.width;
      image.height = sourceCanvas.height;
      clonedCanvas.replaceWith(image);
    } catch {
      // SVG-based charts and browsers that cannot export a canvas still retain
      // the cloned chart element; the rest of the report remains printable.
    }
  });
};

export const clearSharedMobilityPrintMirror = (target) => {
  target?.replaceChildren();
  document
    .querySelectorAll(`[${PRINT_STYLE_ATTRIBUTE}]`)
    .forEach((node) => node.remove());
};

export const prepareSharedMobilityPrintMirror = (iframe, target) => {
  const iframeDocument = iframe?.contentDocument;
  if (!iframeDocument || !target) return false;

  clearSharedMobilityPrintMirror(target);

  const iframeWindow = iframe.contentWindow;
  const pages = Array.from(iframeDocument.querySelectorAll(".page")).filter(
    (page) => {
      if (page.hidden || page.style.display === "none") return false;
      try {
        return iframeWindow?.getComputedStyle(page).display !== "none";
      } catch {
        return true;
      }
    },
  );
  if (!pages.length) return false;

  iframeDocument
    .querySelectorAll('style, link[rel="stylesheet"]')
    .forEach((sourceStyle) => {
      const clonedStyle = sourceStyle.cloneNode(true);
      clonedStyle.setAttribute(PRINT_STYLE_ATTRIBUTE, "true");
      document.head.appendChild(clonedStyle);
    });

  pages.forEach((page) => {
    const clonedPage = page.cloneNode(true);
    copyCanvasImages(page, clonedPage);
    target.appendChild(clonedPage);
  });

  return true;
};

const COPY = {
  en: {
    loading: ["Loading report…", "Please wait while the report is retrieved."],
    expired: [
      "This report has expired",
      "Shared mobility reports are available for 30 days.",
    ],
    not_found: [
      "Report not found",
      "This link is invalid or the report is no longer available.",
    ],
    error: ["Could not load report", "Please try again later."],
    unsupported: [
      "Unsupported report",
      "This report was created with a format this app cannot display.",
    ],
    title: "Mobility Report",
  },
  ja: {
    loading: ["レポートを読み込み中…", "レポートを取得しています。"],
    expired: [
      "このレポートの有効期限が切れています",
      "共有されたモビリティレポートの有効期間は30日間です。",
    ],
    not_found: [
      "レポートが見つかりません",
      "リンクが無効か、レポートを利用できなくなっています。",
    ],
    error: [
      "レポートを読み込めませんでした",
      "しばらくしてから再試行してください。",
    ],
    unsupported: [
      "対応していないレポートです",
      "このレポートは、現在のアプリで表示できない形式で作成されています。",
    ],
    title: "モビリティレポート",
  },
  zh: {
    loading: ["正在加载报告…", "正在检索报告，请稍候。"],
    expired: ["此报告已过期", "共享的行动能力报告有效期为 30 天。"],
    not_found: ["未找到报告", "此链接无效，或报告已不再可用。"],
    error: ["无法加载报告", "请稍后重试。"],
    unsupported: ["不支持的报告", "此报告的格式当前应用无法显示。"],
    title: "行动能力报告",
  },
};

const SharedMobilityReport = () => {
  const { slug } = useParams();
  const [state, setState] = useState({ status: "loading", report: null });
  const iframeRef = useRef(null);
  const printRootRef = useRef(null);

  useEffect(() => {
    let active = true;
    setState({ status: "loading", report: null });
    getShareableReport(slug)
      .then((report) => {
        if (active) setState({ status: "ok", report });
      })
      .catch((error) => {
        if (!active) return;
        if (error?.response?.status === 410) {
          setState({ status: "expired", report: null });
        } else if (error?.response?.status === 404) {
          setState({ status: "not_found", report: null });
        } else {
          setState({ status: "error", report: null });
        }
      });
    return () => {
      active = false;
    };
  }, [slug]);

  const { report } = state;
  const mode = report?.data?.mode;
  const templateUrl = mode ? PRINT_TEMPLATES[mode] : "";
  const locale = ["ja", "zh"].includes(report?.data?.locale)
    ? report.data.locale
    : "en";
  const copy = COPY[locale];

  const iframeSrc = useMemo(() => {
    if (state.status !== "ok" || !templateUrl || !report?.data?.payload) {
      return "";
    }
    return `${templateUrl}#data=${encodePayload({
      ...report.data.payload,
      locale,
    })}`;
  }, [locale, report, state.status, templateUrl]);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle(LETTER_CLASS, LETTER_MODES.has(mode));
    return () => root.classList.remove(LETTER_CLASS);
  }, [mode]);

  useEffect(() => {
    const preparePrint = () => {
      prepareSharedMobilityPrintMirror(iframeRef.current, printRootRef.current);
    };
    const clearPrint = () => {
      clearSharedMobilityPrintMirror(printRootRef.current);
    };

    window.addEventListener("beforeprint", preparePrint);
    window.addEventListener("afterprint", clearPrint);
    return () => {
      window.removeEventListener("beforeprint", preparePrint);
      window.removeEventListener("afterprint", clearPrint);
      clearPrint();
    };
  }, [iframeSrc]);

  if (state.status !== "ok") {
    const statusCopy = copy[state.status];
    return (
      <main className="shared-mobility-state" lang={locale}>
        <AppReportToolbar />
        <div className="shared-mobility-brand">SeniorLife.AI</div>
        <section role={state.status === "loading" ? "status" : "alert"}>
          <h1>{statusCopy[0]}</h1>
          <p>{statusCopy[1]}</p>
        </section>
      </main>
    );
  }

  if (
    report?.version !== "v1" ||
    report?.data?.kind !== "mobility_report" ||
    !templateUrl ||
    !report?.data?.payload
  ) {
    return (
      <main className="shared-mobility-state" lang={locale}>
        <AppReportToolbar />
        <div className="shared-mobility-brand">SeniorLife.AI</div>
        <section role="alert">
          <h1>{copy.unsupported[0]}</h1>
          <p>{copy.unsupported[1]}</p>
        </section>
      </main>
    );
  }

  return (
    <>
      <div className="shared-mobility-frame-wrap" lang={locale}>
        <AppReportToolbar />
        <iframe
          ref={iframeRef}
          title={copy.title}
          className="shared-mobility-frame"
          src={iframeSrc}
        />
      </div>
      <div
        ref={printRootRef}
        className="shared-mobility-print-root"
        lang={locale}
      />
    </>
  );
};

export default SharedMobilityReport;
